import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ListChecks, Plus, Search, Circle, CheckCircle2, Pencil, Trash2, CalendarDays,
  ScrollText, FileText, ClipboardList, Flame, User as UserIcon, Loader2, X,
} from "lucide-react";

const TIPO_META = {
  notarial: { label: "Notarial", icon: ScrollText, color: "#7A1E2E", bg: "#FBEDEF" },
  legal:    { label: "Legal",    icon: FileText,   color: "#4A6D8C", bg: "#DDE9F3" },
  generica: { label: "Tarea",    icon: ClipboardList, color: "#B88537", bg: "#FBEED4" },
};

const PRIORIDAD_META = {
  alta:  { label: "Alta",  color: "#DC2626", bg: "#FADCDC", dot: "#DC2626" },
  media: { label: "Media", color: "#D97706", bg: "#FEE9CC", dot: "#D97706" },
  baja:  { label: "Baja",  color: "#6B7280", bg: "#F3F4F6", dot: "#9CA3AF" },
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const EMPTY = { titulo: "", descripcion: "", tipo: "generica", subtipo: "", fecha_limite: "", prioridad: "media", cliente_id: "", asignados: [], notas: "" };

function fmtDate(iso) {
  if (!iso) return "";
  const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
  const [y, m, d] = iso.split("-");
  if (!y) return iso;
  return `${d} ${meses[parseInt(m,10)-1]} ${y}`;
}

export default function Solicitudes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pendientes: 0, completadas: 0 });
  const [config, setConfig] = useState({ tipos: [], prioridades: [], tipos_escritura: [], tipos_legal: [], tipos_genericas: [] });
  const [clientes, setClientes] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("pendientes"); // pendientes | realizadas | todas
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (tab === "pendientes") params.estado = "pendiente";
    if (tab === "realizadas") params.estado = "completada";
    if (q.trim()) params.q = q.trim();
    const [lst, st] = await Promise.all([
      api.get("/solicitudes", { params }),
      api.get("/solicitudes/stats"),
    ]);
    setItems(lst.data || []);
    setStats(st.data || {});
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cfg, cli, usr] = await Promise.all([
          api.get("/solicitudes/config"),
          api.get("/clientes"),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        setConfig(cfg.data);
        setClientes(cli.data || []);
        setUsers(usr.data || []);
      } catch (_) {}
      await load();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, q]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, tipo: "generica", prioridad: "media" });
    setDialog(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      titulo: s.titulo || "",
      descripcion: s.descripcion || "",
      tipo: s.tipo || "generica",
      subtipo: s.subtipo || "",
      fecha_limite: s.fecha_limite || "",
      prioridad: s.prioridad || "media",
      cliente_id: s.cliente_id || "",
      asignados: s.asignados || [],
      notas: s.notas || "",
    });
    setDialog(true);
  };

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || null,
        asignados: form.asignados || [],
      };
      if (editing) {
        await api.put(`/solicitudes/${editing.id}`, payload);
        toast.success("Solicitud actualizada");
      } else {
        await api.post(`/solicitudes`, payload);
        toast.success("Solicitud creada · notificación enviada");
      }
      setDialog(false);
      window.dispatchEvent(new Event("mensajes:refresh"));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try {
      await api.post(`/solicitudes/${s.id}/toggle`);
      await load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const eliminar = async (s) => {
    if (!window.confirm(`¿Eliminar la solicitud "${s.titulo}"?`)) return;
    try {
      await api.delete(`/solicitudes/${s.id}`);
      toast.success("Solicitud eliminada");
      await load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const clienteMap = useMemo(() => Object.fromEntries(clientes.map((c) => [c.id, c.nombre])), [clientes]);

  const subtipoOpciones = useMemo(() => {
    if (form.tipo === "notarial") return config.tipos_escritura || [];
    if (form.tipo === "legal") return config.tipos_legal || [];
    if (form.tipo === "generica") return config.tipos_genericas || [];
    return [];
  }, [form.tipo, config]);

  return (
    <div className="space-y-6" data-testid="module-solicitudes">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
            <ListChecks className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.8}/>
          </div>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A0810]">Solicitudes</h1>
            <p className="mt-1 text-sm text-[#4a3838]">Lo que el bufete pidió generar — pendientes y realizadas.</p>
          </div>
        </div>
        <Button data-testid="btn-nueva-solicitud" onClick={openNew} className="h-11 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
          <Plus className="h-4 w-4 mr-2" strokeWidth={2}/> Nueva Solicitud
        </Button>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 bg-white rounded-lg border border-[#E5DFD3] p-1">
          {[
            { key: "pendientes", label: "Pendientes", count: stats.pendientes, color: "#D97706" },
            { key: "realizadas", label: "Realizadas", count: stats.completadas, color: "#16A34A" },
            { key: "todas", label: "Todas", count: stats.total, color: "#6B5D57" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
                tab === t.key ? "bg-[#F5EEE0] text-[#7A1E2E]" : "text-[#6B5D57] hover:text-[#1A0810]"
              }`}
            >
              {t.label}
              <span
                className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold"
                style={{ backgroundColor: tab === t.key ? `${t.color}20` : "#F5EEE0", color: tab === t.key ? t.color : "#6B5D57" }}
              >{t.count}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8}/>
          <Input
            data-testid="input-search-solicitudes"
            placeholder="Buscar..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 pl-10 bg-white border-[#E5DFD3]"
          />
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {loading && (
          <div className="p-10 text-center text-[#6B5D57]"><Loader2 className="h-5 w-5 mx-auto animate-spin"/></div>
        )}
        {!loading && items.length === 0 && (
          <div className="rounded-xl bg-white border-2 border-dashed border-[#E5DFD3] p-12 text-center">
            <ListChecks className="h-8 w-8 mx-auto text-[#C25A5A]" strokeWidth={1.5}/>
            <div className="mt-3 font-display text-lg text-[#1A0810]">Sin solicitudes</div>
            <div className="mt-1 text-xs text-[#6B5D57]">Crea una nueva solicitud para comenzar.</div>
          </div>
        )}
        {!loading && items.map((s) => {
          const tm = TIPO_META[s.tipo] || TIPO_META.generica;
          const pm = PRIORIDAD_META[s.prioridad] || PRIORIDAD_META.media;
          const TIcon = tm.icon;
          const done = s.estado === "completada";
          return (
            <div
              key={s.id}
              data-testid={`solicitud-row-${s.id}`}
              className={`rounded-xl bg-white border border-[#E5DFD3] p-4 flex items-start gap-4 hover:border-[#C25A5A] transition-colors ${done ? "opacity-70" : ""}`}
              style={{ borderLeft: `4px solid ${pm.dot}` }}
            >
              <button
                data-testid={`toggle-${s.id}`}
                onClick={() => toggle(s)}
                className="mt-1 flex-shrink-0"
                title={done ? "Marcar como pendiente" : "Marcar como realizada"}
              >
                {done
                  ? <CheckCircle2 className="h-5 w-5 text-[#16A34A]" strokeWidth={2.2} fill="#D8EFDD"/>
                  : <Circle className="h-5 w-5 text-[#C4B7A8] hover:text-[#7A1E2E]" strokeWidth={2}/>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className={`text-[15px] font-semibold text-[#1A0810] ${done ? "line-through" : ""}`}>
                    {s.titulo}
                  </div>
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: tm.bg, color: tm.color }}
                  >
                    <TIcon className="h-2.5 w-2.5" strokeWidth={2.5}/>
                    {tm.label}
                    {s.subtipo ? ` · ${s.subtipo}` : ""}
                  </span>
                  {s.prioridad === "alta" && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: pm.bg, color: pm.color }}>
                      <Flame className="h-2.5 w-2.5" strokeWidth={2.5} fill={pm.color}/> Urgente
                    </span>
                  )}
                </div>
                {s.descripcion && (
                  <div className="mt-1 text-xs text-[#6B5D57] leading-relaxed">{s.descripcion}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[#6B5D57]">
                  {s.cliente_id && clienteMap[s.cliente_id] && (
                    <span className="inline-flex items-center gap-1">
                      <UserIcon className="h-3 w-3" strokeWidth={1.8}/> {clienteMap[s.cliente_id]}
                    </span>
                  )}
                  {s.asignados_nombres && s.asignados_nombres.length > 0 && (
                    <span className="inline-flex items-center gap-1 flex-wrap">
                      <UserIcon className="h-3 w-3" strokeWidth={1.8}/> Asignado:
                      {s.asignados_nombres.map((n, i) => (
                        <b key={i} className="text-[#3d2a2a]">{n}{i < s.asignados_nombres.length - 1 ? "," : ""}</b>
                      ))}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0 flex flex-col items-end">
                {s.fecha_limite && (
                  <div className="inline-flex items-center gap-1 text-[11px] text-[#6B5D57]">
                    <CalendarDays className="h-3 w-3" strokeWidth={1.8}/> {fmtDate(s.fecha_limite)}
                  </div>
                )}
                <div className="text-[11px] text-[#6B5D57] mt-0.5">
                  Por <b className="text-[#3d2a2a]">{s.created_by_name}</b>
                </div>
                <div className="mt-2 inline-flex gap-1">
                  <button
                    data-testid={`edit-${s.id}`}
                    onClick={() => openEdit(s)}
                    className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E]"
                    title="Editar"
                  >
                    <Pencil className="h-4 w-4" strokeWidth={1.8}/>
                  </button>
                  <button
                    data-testid={`delete-${s.id}`}
                    onClick={() => eliminar(s)}
                    className="h-8 w-8 rounded-md hover:bg-red-50 flex items-center justify-center text-[#6B5D57] hover:text-red-700"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.8}/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl bg-[#FAF7F0] border-[#E5DFD3] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">
              {editing ? "Editar solicitud" : "Nueva solicitud"}
            </DialogTitle>
            <DialogDescription className="text-[#6B5D57]">
              Delega una tarea, escritura o documento a algún miembro del equipo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {config.tipos?.map((t) => {
                const TIcon = TIPO_META[t.key]?.icon || ClipboardList;
                const active = form.tipo === t.key;
                return (
                  <button
                    key={t.key}
                    data-testid={`select-tipo-${t.key}`}
                    onClick={() => setForm({ ...form, tipo: t.key, subtipo: "" })}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      active ? "bg-[#F5EEE0] border-[#7A1E2E]" : "bg-white border-[#E5DFD3] hover:border-[#C25A5A]"
                    }`}
                  >
                    <TIcon className="h-4 w-4" strokeWidth={1.8} style={{ color: TIPO_META[t.key]?.color }}/>
                    <div className="mt-1.5 text-xs font-semibold text-[#1A0810]">{t.label}</div>
                  </button>
                );
              })}
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Título *</Label>
              <Input data-testid="form-titulo" value={form.titulo} onChange={(e)=>setForm({...form, titulo: e.target.value})} className={`mt-2 ${inputCls}`} placeholder="Ej: Preparar escritura de traspaso"/>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">
                {form.tipo === "notarial" ? "Tipo de escritura" : form.tipo === "legal" ? "Tipo de documento" : "Categoría de tarea"}
              </Label>
              <Select value={form.subtipo || undefined} onValueChange={(v)=>setForm({...form, subtipo: v})}>
                <SelectTrigger data-testid="form-subtipo" className={`mt-2 ${inputCls}`}><SelectValue placeholder="Selecciona…"/></SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {subtipoOpciones.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Descripción</Label>
              <Textarea data-testid="form-descripcion" value={form.descripcion} onChange={(e)=>setForm({...form, descripcion: e.target.value})} className="mt-2 bg-white border-[#E5DFD3]" placeholder="Detalles de lo que se necesita…"/>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Fecha límite</Label>
                <Input data-testid="form-fecha" type="date" value={form.fecha_limite} onChange={(e)=>setForm({...form, fecha_limite: e.target.value})} className={`mt-2 ${inputCls}`}/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v)=>setForm({...form, prioridad: v})}>
                  <SelectTrigger data-testid="form-prioridad" className={`mt-2 ${inputCls}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {config.prioridades?.map((p)=><SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Asignar a ({form.asignados?.length || 0})</Label>
                <div className="mt-2 rounded-md border border-[#E5DFD3] bg-white p-2 max-h-[140px] overflow-y-auto space-y-1">
                  {users.filter((u) => u.id !== user?.id).map((u) => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F5EEE0] px-1.5 py-1 rounded text-sm">
                      <input
                        type="checkbox"
                        data-testid={`form-asignado-${u.id}`}
                        checked={(form.asignados || []).includes(u.id)}
                        onChange={() => {
                          const list = form.asignados || [];
                          const next = list.includes(u.id) ? list.filter((x) => x !== u.id) : [...list, u.id];
                          setForm({ ...form, asignados: next });
                        }}
                        className="h-3.5 w-3.5 accent-[#7A1E2E]"
                      />
                      <span className="text-[#1A0810]">{u.name || u.email}</span>
                      <span className="text-[11px] text-[#6B5D57]">· {u.role}</span>
                    </label>
                  ))}
                  {users.filter((u) => u.id !== user?.id).length === 0 && (
                    <div className="text-xs text-[#6B5D57] p-2">Sin otros usuarios.</div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Cliente (opcional)</Label>
              <Select value={form.cliente_id || "none"} onValueChange={(v)=>setForm({...form, cliente_id: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="form-cliente" className={`mt-2 ${inputCls}`}><SelectValue placeholder="Sin cliente"/></SelectTrigger>
                <SelectContent className="max-h-[240px]">
                  <SelectItem value="none">Sin cliente vinculado</SelectItem>
                  {clientes.map((c)=><SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Notas internas</Label>
              <Textarea data-testid="form-notas" value={form.notas} onChange={(e)=>setForm({...form, notas: e.target.value})} className="mt-2 bg-white border-[#E5DFD3] min-h-[64px]" placeholder="Referencias, contexto adicional…"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDialog(false)} className="h-10 border-[#E5DFD3]">Cancelar</Button>
            <Button data-testid="btn-save-solicitud" onClick={save} disabled={saving} className="h-10 bg-[#7A1E2E] hover:bg-[#5F1723] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <Plus className="h-4 w-4 mr-2" strokeWidth={2}/>}
              {editing ? "Guardar cambios" : "Crear solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
